import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
  DateRange,
  MatCalendar,
  MatCalendarHeader,
  MatDatepickerActions,
  MatDateRangePicker
} from '@angular/material/datepicker';
import { CurrentUserService } from '../../services/currentUser.service';
import moment, { Moment } from 'moment';
import { RANGE_PRESETS, RangePreset } from '../../model/dates-range-preset.model';
import { MatButton } from '@angular/material/button';
import { NgClass } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { skip } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  templateUrl: './datepicker-range.component.html',
  imports: [MatCalendarHeader, MatButton, NgClass, MatDatepickerActions]
})
export class DatepickerRangeComponent implements OnInit {
  readonly rangePresets = RANGE_PRESETS;
  selectedDateRange = '';
  timezone: string;

  #destroyRef = inject(DestroyRef);

  constructor(
    private calendar: MatCalendar<Moment>,
    private currentUserService: CurrentUserService,
    private picker: MatDateRangePicker<Moment>
  ) {}

  ngOnInit(): void {
    this.timezone = this.currentUserService?.timeZone;
    this.selectedDateRange = sessionStorage.getItem('selectedDateRange');

    this.calendar.selectedChange
      .pipe(skip(1), debounceTime(100), takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        const selectedDates = this.calendar.selected as DateRange<Moment>;
        if (selectedDates?.start && selectedDates?.end) {
          this.picker.select(selectedDates.start);
          this.picker.select(selectedDates.end);
          this.picker.close();
        }
      });
  }

  selectRange(range: RangePreset): void {
    const [startDate, endDate] = this.pickDateRange(range);
    const selectedDates = this.calendar.selected as DateRange<Moment>;
    if (selectedDates.start && !selectedDates.end) {
      this.picker.select(endDate);
    }
    this.picker.select(startDate);
    this.picker.select(endDate);
    this.picker.close();
    sessionStorage.setItem('selectedDateRange', range);
  }

  private pickDateRange(range: RangePreset): [Moment, Moment] {
    this.selectedDateRange = range;
    let startDate: Moment;
    let endDate: Moment;

    switch (range) {
      case 'Last 30 days':
        startDate = this.timezone
          ? moment().tz(this.timezone).subtract(30, 'days').startOf('day')
          : moment().subtract(30, 'days').startOf('day');
        endDate = this.timezone ? moment().tz(this.timezone).endOf('day') : moment().endOf('day');
        return [startDate, endDate];
      case 'Last 90 days':
        startDate = this.timezone
          ? moment().tz(this.timezone).subtract(90, 'days').startOf('day')
          : moment().subtract(90, 'days').startOf('day');
        endDate = this.timezone ? moment().tz(this.timezone).endOf('day') : moment().endOf('day');
        return [startDate, endDate];
      case 'This month':
        startDate = this.timezone ? moment().tz(this.timezone).startOf('month') : moment().startOf('month');
        endDate = this.timezone ? moment().tz(this.timezone).endOf('month') : moment().endOf('month');
        return [startDate, endDate];
      case 'Last month':
        startDate = this.timezone
          ? moment().tz(this.timezone).subtract(1, 'month').startOf('month')
          : moment().subtract(1, 'month').startOf('month');
        endDate = this.timezone
          ? moment().tz(this.timezone).subtract(1, 'month').endOf('month')
          : moment().subtract(1, 'month').endOf('month');
        return [startDate, endDate];
      case 'This year':
        startDate = this.timezone ? moment().tz(this.timezone).startOf('year') : moment().startOf('year');
        endDate = this.timezone ? moment().tz(this.timezone).endOf('year') : moment().endOf('year');
        return [startDate, endDate];
      default:
        return [null, null];
    }
  }
}
